import React, { FC, useState } from "react";
import { FieldArray, useFormikContext } from "formik";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { DeleteForeverOutlined } from "@mui/icons-material";
import Grid from "../../Unknown/Grid";
import FormikRichTextField from "../../Unknown/FormikRichTextField";
import FormikTextField from "../../Unknown/FormikTextField";
import { FormValues, Translations, FindingSeverity } from "./types";
import Typography from "../../Unknown/Typography";
import Box from "../../Unknown/Box";
import MenuItem from "../../Unknown/MenuItem";
import IconButton from "../../Unknown/IconButton";
import Button from "../../Unknown/Button";
import Alert from "../../Unknown/Alert";
import AlertTitle from "../../Unknown/AlertTitle";

const CONFIRM_COLOR = "#541313";

type DeleteConfirmAlertProps = {
  message: string;
  deleteLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
};

const DeleteConfirmAlert: FC<DeleteConfirmAlertProps> = ({
  message,
  deleteLabel,
  onConfirm,
  onCancel,
}) => (
  <ClickAwayListener onClickAway={onCancel}>
    <Alert
      severity="error"
      sx={{ marginBottom: 4, paddingY: 1.5 }}
      action={
        <Button
          size="small"
          sx={{ color: CONFIRM_COLOR, marginRight: 2 }}
          onClick={onConfirm}
        >
          {deleteLabel}
        </Button>
      }
    >
      <AlertTitle sx={{ color: CONFIRM_COLOR, mb: 0 }}>{message}</AlertTitle>
    </Alert>
  </ClickAwayListener>
);

export type AppointmentAIReportReviewEditFormProps = {
  translations: Translations;
};

const AppointmentAIReportReviewEditForm: FC<
  AppointmentAIReportReviewEditFormProps
> = ({ translations }) => {
  const { isSubmitting, values } = useFormikContext<FormValues>();

  const [confirmingFindingIndex, setConfirmingFindingIndex] = useState<
    number | null
  >(null);
  const [confirmingNextStepIndex, setConfirmingNextStepIndex] = useState<
    number | null
  >(null);

  const severityOptions: { value: FindingSeverity; label: string }[] = [
    {
      value: "action_required",
      label: translations.severityActionRequired || "",
    },
    {
      value: "no_action_required",
      label: translations.severityMnoActionRequired || "",
    },
    {
      value: "requires_attention",
      label: translations.severityRequiresAttention || "",
    },
  ];

  return (
    <Grid container spacing={4} pt={3}>
      <Grid item xs={12}>
        <FormikRichTextField
          name="summarySection"
          label={translations.labelSummary}
          editable={!isSubmitting}
        />
      </Grid>
      {values.findingsSection.length > 0 && (
        <Grid item xs={12}>
          <Box p={4} borderRadius={1} bgcolor="grey.100">
            <Typography variant="h6" mb={4}>
              {translations.labelFindings}
            </Typography>
            <FieldArray name="findingsSection">
              {({ remove }) => (
                <Box display="flex" gap={4} flexDirection="column">
                  {values.findingsSection.map((findingItem, index) => (
                    <Box
                      // eslint-disable-next-line react/no-array-index-key
                      key={`finding-${index}`}
                      gap={4}
                      p={3}
                      bgcolor="common.white"
                      borderRadius={1}
                    >
                      {confirmingFindingIndex === index && (
                        <DeleteConfirmAlert
                          message={translations.confirmDelete || ""}
                          deleteLabel={translations.btnDelete || ""}
                          onConfirm={() => {
                            remove(index);
                            setConfirmingFindingIndex(null);
                          }}
                          onCancel={() => setConfirmingFindingIndex(null)}
                        />
                      )}
                      <Box display="flex" flexDirection="column" gap={4}>
                        <Box display="flex" gap={4} alignItems="center">
                          <FormikTextField
                            name={`findingsSection.${index}.title`}
                            fullWidth
                            label={translations.labelFindingTitle}
                            disabled={isSubmitting}
                            required
                          />
                          <Box minWidth={220}>
                            <FormikTextField
                              name={`findingsSection.${index}.severity`}
                              select
                              fullWidth
                              label={translations.labelFindingSeverity}
                              disabled={isSubmitting}
                              required
                            >
                              {severityOptions.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </FormikTextField>
                          </Box>
                          {confirmingFindingIndex !== index && (
                            <IconButton
                              onClick={() => setConfirmingFindingIndex(index)}
                              disabled={isSubmitting}
                              color="error"
                              size="large"
                            >
                              <DeleteForeverOutlined />
                            </IconButton>
                          )}
                        </Box>
                        <Box>
                          <FormikTextField
                            name={`findingsSection.${index}.whatThisMeans`}
                            multiline
                            minRows={2}
                            required
                            fullWidth
                            label={translations.labelFindingWhatThisMeans}
                            disabled={isSubmitting}
                          />
                        </Box>
                        <Box>
                          <FormikTextField
                            name={`findingsSection.${index}.whyItMatters`}
                            multiline
                            minRows={2}
                            required
                            fullWidth
                            label={translations.labelFindingWhyItMatters}
                            disabled={isSubmitting}
                          />
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </FieldArray>
          </Box>
        </Grid>
      )}

      {values.nextStepsSection.length > 0 && (
        <Grid item xs={12}>
          <Box p={4} borderRadius={1} bgcolor="grey.100">
            <Typography variant="h6" mb={4}>
              {translations.labelNextSteps}
            </Typography>
            <FieldArray name="nextStepsSection">
              {({ remove }) => (
                <Box display="flex" gap={4} flexDirection="column">
                  {values.nextStepsSection.map((nextStepItem, index) => (
                    <Box
                      // eslint-disable-next-line react/no-array-index-key
                      key={`nextstep-${index}`}
                      gap={4}
                      p={3}
                      bgcolor="common.white"
                      borderRadius={1}
                    >
                      {confirmingNextStepIndex === index && (
                        <DeleteConfirmAlert
                          message={translations.confirmDelete || ""}
                          deleteLabel={translations.btnDelete || ""}
                          onConfirm={() => {
                            remove(index);
                            setConfirmingNextStepIndex(null);
                          }}
                          onCancel={() => setConfirmingNextStepIndex(null)}
                        />
                      )}
                      <Box display="flex" flexDirection="column" gap={4}>
                        <Box display="flex" gap={4} alignItems="flex-start">
                          <FormikTextField
                            name={`nextStepsSection.${index}.title`}
                            fullWidth
                            label={translations.labelNextStepTitle}
                            disabled={isSubmitting}
                            required
                          />
                          {confirmingNextStepIndex !== index && (
                            <IconButton
                              onClick={() => setConfirmingNextStepIndex(index)}
                              disabled={isSubmitting}
                              color="error"
                              size="large"
                            >
                              <DeleteForeverOutlined />
                            </IconButton>
                          )}
                        </Box>
                        <Box>
                          <FormikTextField
                            name={`nextStepsSection.${index}.description`}
                            multiline
                            minRows={2}
                            required
                            fullWidth
                            label={translations.labelNextStepDescription}
                            disabled={isSubmitting}
                          />
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </FieldArray>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default AppointmentAIReportReviewEditForm;
